import { useCallback, useEffect, useMemo, useState } from "react";

import { rulesApi, type ProjectRuleAccess } from "../api/rules";
import { useAuth } from "../auth";
import {
  effectivePermissions,
  rulesGates,
  unknownRulesGates,
  type RulesGates,
} from "../lib/rulesPermissions";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The caller's rule access on one project (GET /api/v1/dq-rules/access).
 *
 * - `none`: no (real) project, so there is no project role to ask about;
 * - `loading` / `failed`: the answer is not known. Rule powers come ONLY from
 *   the project role (DQ rule governance), so the token's permissions say
 *   nothing useful here, in either direction;
 * - `loaded`: `access` holds the answer.
 */
export interface ProjectRuleAccessState {
  status: "none" | "loading" | "loaded" | "failed";
  access: ProjectRuleAccess | null;
  retry: () => void;
}

// Concurrent mounts for the same project (the profiler page and the rules view
// it embeds) share one request, but only for SHARE_MS: a later mount asks again
// (and sees a role changed in the meantime), and a request that hangs cannot
// hold every later mount in "loading".
const SHARE_MS = 2000;
const inflight = new Map<
  string,
  { at: number; request: Promise<ProjectRuleAccess> }
>();

function fetchAccess(projectId: string): Promise<ProjectRuleAccess> {
  const existing = inflight.get(projectId);
  if (existing && Date.now() - existing.at < SHARE_MS) return existing.request;
  const request = rulesApi.access(projectId).finally(() => {
    if (inflight.get(projectId)?.request === request)
      inflight.delete(projectId);
  });
  inflight.set(projectId, { at: Date.now(), request });
  return request;
}

/** Tests only: forget shared requests between tests. */
export function resetProjectRuleAccessForTests(): void {
  inflight.clear();
}

export function useProjectRuleAccess(
  projectId: string | null | undefined,
): ProjectRuleAccessState {
  const hasProject = Boolean(projectId && UUID_RE.test(projectId));
  const [access, setAccess] = useState<ProjectRuleAccess | null>(null);
  const [status, setStatus] = useState<ProjectRuleAccessState["status"]>(
    hasProject ? "loading" : "none",
  );
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setAccess(null);
    if (!projectId || !UUID_RE.test(projectId)) {
      setStatus("none");
      return;
    }
    setStatus("loading");
    let cancelled = false;
    fetchAccess(projectId)
      .then((value) => {
        if (cancelled) return;
        if (value && Array.isArray(value.permissions)) {
          setAccess(value);
          setStatus("loaded");
        } else {
          setStatus("failed");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { status, access, retry };
}

/**
 * The signed-in user's `rules:*` gates (see lib/rulesPermissions.ts).
 *
 * DQ rule governance: rule powers come from the user's role ON THE PROJECT,
 * so with a project the gates come from the project's /access answer. While it
 * loads, or if it fails, every gate is closed as "unknown" (with a neutral
 * reason, and no permission notice). Only `platform_admin` is decided from the
 * token: it is a wildcard on the backend by role name and may arrive with no
 * permissions. Without a project the token's permissions stand in. Either way
 * the routes decide.
 */
export function useRulesGates(projectId?: string | null): RulesGates {
  return useRulesGatesFor(useProjectRuleAccess(projectId));
}

export function useRulesGatesFor(state: ProjectRuleAccessState): RulesGates {
  const { user } = useAuth();
  const role = user?.role;
  const permissions = user?.permissions;
  const { status, access } = state;
  return useMemo(() => {
    if (role === "platform_admin" || status === "none") {
      return rulesGates(effectivePermissions(role, permissions));
    }
    if (status === "loaded" && access) {
      return rulesGates(access.permissions, access.project_role_label);
    }
    return unknownRulesGates(
      status === "failed"
        ? "Could not check your rule role on this project; reload the page to try again."
        : "Checking your rule role on this project…",
    );
  }, [status, access, role, permissions]);
}
